import { EntityType } from '@frontend/common';
import { EquipmentExtended, IEquipment } from '@frontend/equipment/types';
import { QuestionnaireClient } from '@frontend/group/api';
import { ProductClient } from '@frontend/product/api';
import { IProduct, ProductType } from '@frontend/product/types';
import { AnswerClient, QuestionClient } from '@frontend/question/api';
import { Answer, Question } from '@frontend/question/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useScreen } from '../useScreen';

export interface QuestionWithAnswers {
    question: Question;
    answers: Answer[];
}

export const useReturnDetail = () => {
    const location = useLocation();
    const { store, onBack, onSubmit, updateWizardData } = useScreen();
    const [isLoading, setIsLoading] = useState(true);

    const stateEquipment = location.state?.equipment as IEquipment | undefined;
    const product = store?.data?.product as IProduct | undefined;
    const equipment = store?.data?.equipment as Partial<EquipmentExtended> | undefined;

    const [questionsWithAnswers, setQuestionsWithAnswers] = useState<Map<string, QuestionWithAnswers>>(new Map());
    const [selectedAnswers, setSelectedAnswers] = useState<Answer[]>([]);
    const [hasSkipped, setHasSkipped] = useState(false);

    useEffect(() => {
        if (product && equipment) return;
        if (product && !equipment && product.type === ProductType.CONSUMABLE) return;
        if (equipment) {
            if (!product) {
                ProductClient.fetchProduct(equipment.account_id!, equipment.product_id!).then((product) => {
                    updateWizardData({
                        product
                    });
                });
            }
            return;
        }
        if (product) {
            //We only have the product so there is not really anything we can do.
            //TODO show error message telling the user the product is not recognized
        }
        if (stateEquipment) {
            ProductClient.fetchProduct(stateEquipment.account_id, stateEquipment.product_id).then((product) => {
                updateWizardData({
                    product,
                    equipment: stateEquipment
                });
            });
        }
        return;
    }, [equipment, product]);

    // Fetch questionnaire data
    useEffect(() => {
        if (product) {
            if (product.return_questionnaire_id) {
                fetchQuestionnaire(product)
                    .then((questionsMap) => {
                        setQuestionsWithAnswers(questionsMap);
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            } else {
                setIsLoading(false);
                if (!hasSkipped) {
                    setHasSkipped(true);
                    const nextStep = store?.steps.find((step) => step.screen === ScreenType.RETURN_DETAIL && step.trigger === ScreenTrigger.SUBMIT);
                    onSubmit(nextStep);
                }
            }
        }
    }, [product]);

    const handleBack = () => {
        onBack();
    };

    const handleSubmit = useCallback(() => {
        // Store the selected answers in wizard data for confirm-return to use
        updateWizardData({ answers: selectedAnswers });
        onSubmit();
    }, [selectedAnswers, updateWizardData, onSubmit]);

    return {
        product,
        equipment,

        questionsWithAnswers,
        selectedAnswers,
        setSelectedAnswers,
        isLoading,
        onBack: handleBack,
        onSubmit: handleSubmit
    };
};

async function fetchQuestionnaire(product: IProduct) {
    const questionnaire = await QuestionnaireClient.fetchQuestionnaire(product.account_id, product.return_questionnaire_id!);
    const members = await QuestionnaireClient.fetchQuestionnaireMembers({
        group_id: questionnaire.id,
        entity_type: EntityType.QUESTION
    });
    const questions = await Promise.all(
        members.results.map(async (member) => {
            return await QuestionClient.fetchQuestion(member.account_id, member.entity_id);
        })
    );

    const questionsMap = new Map<string, QuestionWithAnswers>();

    await Promise.all(
        questions.map(async (question) => {
            const answersResponse = await AnswerClient.fetchAnswers({ question_id: question.id });
            questionsMap.set(question.id, {
                question,
                answers: answersResponse.results
            });
        })
    );

    return questionsMap;
}
